<template>
  <div class="inv-page" v-loading="loading">

    <div class="inv-header">
      <div class="inv-inner inv-header-inner">
        <h1 class="inv-title">Invoices</h1>
        <div class="inv-header-actions">
          <select v-model="filterStatus" @change="onFilterChange" class="inv-select">
            <option value="">All statuses</option>
            <!-- No "Draft": the TMS never sends a customer an unsent draft. -->
            <option value="sent">Sent</option>
            <option value="partially_paid">Partially Paid</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select v-model="filterYear" @change="onFilterChange" class="inv-select">
            <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
          </select>
          <button class="inv-btn inv-btn--csv" @click="downloadCSV"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/><line x1="10" y1="9" x2="14" y2="9"/></svg> CSV</button>
        </div>
      </div>
    </div>

    <div class="inv-inner inv-list-wrap">
      <div class="inv-table">

        <div class="inv-thead">
          <div class="inv-th inv-th--inv">INVOICE</div>
          <div class="inv-th inv-th--period">PERIOD</div>
          <div class="inv-th inv-th--due">DUE</div>
          <div class="inv-th inv-th--total">TOTAL</div>
          <div class="inv-th inv-th--paid">AMOUNT PAID</div>
          <div class="inv-th inv-th--outstanding">OUTSTANDING</div>
          <div class="inv-th inv-th--status">STATUS</div>
          <div class="inv-th inv-th--action"></div>
        </div>

        <div class="inv-tbody">
          <div
            v-for="inv in paged"
            :key="inv.id"
            class="inv-row-wrap"
          >
            <div class="inv-row">
              <div class="inv-td inv-td--inv">
                <span class="inv-ref">{{ inv.invoice_number }}</span>
                <span class="inv-sub">{{ fmtDate(inv.created_at) }}</span>
              </div>
              <div class="inv-td inv-td--period">
                <span class="inv-td-text">{{ fmtDate(inv.period_start) }} – {{ fmtDate(inv.period_end) }}</span>
              </div>
              <div class="inv-td inv-td--due">
                <span :class="isOverdue(inv) ? 'inv-overdue' : 'inv-td-muted'">
                  {{ inv.due_date ? fmtDate(inv.due_date) : '—' }}
                  <span v-if="isOverdue(inv)" class="inv-overdue-tag">Overdue</span>
                </span>
              </div>
              <div class="inv-td inv-td--total">
                <span class="inv-amount">{{ currency }} {{ fmt(inv.total) }}</span>
              </div>
              <div class="inv-td inv-td--paid">
                <span :class="paidClass(inv)">
                  {{ inv.status === 'cancelled' ? '—' : currency + ' ' + fmt(inv.amount_paid) }}
                </span>
              </div>
              <div class="inv-td inv-td--outstanding">
                <span :class="outstandingClass(inv)">
                  {{ inv.status === 'cancelled' ? '—' : currency + ' ' + fmt(inv.outstanding) }}
                </span>
              </div>
              <div class="inv-td inv-td--status">
                <span class="inv-badge" :class="statusClass(inv.status)">{{ statusLabel(inv.status) }}</span>
              </div>
              <div class="inv-td inv-td--action">
                <button class="inv-view-btn" @click="$router.push('/invoices/' + inv.id)">
                  View
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Pagination -->
      <div v-if="!loading && totalPages > 1" class="inv-pagination">
        <button class="inv-page-btn" :disabled="page === 1" @click="page = page - 1">&#8592; Prev</button>
        <span class="inv-page-info">Page {{ page }} of {{ totalPages }}</span>
        <button class="inv-page-btn" :disabled="page >= totalPages" @click="page = page + 1">Next &#8594;</button>
      </div>

      <div class="inv-error" v-if="!loading && error">
        <svg width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <p>{{ error }}</p>
        <button class="inv-page-btn" @click="load">Retry</button>
      </div>

      <div class="inv-empty" v-if="!loading && !error && invoices.length === 0">
        <svg width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/></svg>
        <p>No invoices found</p>
      </div>

      <!-- Summary bar -->
      <div class="inv-summary" v-if="invoices.length > 0">
        <div class="inv-summary-item">
          <span class="inv-summary-label">Total billed</span>
          <span class="inv-summary-value">{{ currency }} {{ fmt(totalBilled) }}</span>
        </div>
        <div class="inv-summary-item">
          <span class="inv-summary-label">Outstanding</span>
          <span class="inv-summary-value inv-summary-value--red">{{ currency }} {{ fmt(totalOutstanding) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import api from '../../api/index'
import { unparse } from 'papaparse'
import { message } from '../../utils/message'
import { fmtDate } from '../../utils/date'

const loading = ref(false)
const error = ref('')
const invoices = ref<any[]>([])
const filterStatus = ref('')
const filterYear = ref(new Date().getFullYear())
const currency = 'KES'

const page = ref(1)
const pageSize = 10

const years = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i)

const totalBilled      = computed(() => invoices.value.filter(i => i.status !== 'cancelled').reduce((s, i) => s + parseFloat(i.total || 0), 0))
const totalOutstanding = computed(() => invoices.value.filter(i => i.status !== 'cancelled').reduce((s, i) => s + parseFloat(i.outstanding || 0), 0))

const totalPages = computed(() => Math.max(1, Math.ceil(invoices.value.length / pageSize)))
const paged = computed(() => {
  const start = (page.value - 1) * pageSize
  return invoices.value.slice(start, start + pageSize)
})

function onFilterChange() {
  page.value = 1
  load()
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const params: any = { year: filterYear.value }
    if (filterStatus.value) params.status = filterStatus.value
    const { data } = await api.get('/customer/invoices', { params })
    invoices.value = data.data?.invoices ?? []
    page.value = 1
  } catch {
    error.value = 'Failed to load invoices.'
    invoices.value = []
    message.error('Failed to load invoices.')
  } finally {
    loading.value = false
  }
}

function fmt(n: any) {
  return parseFloat(n || 0).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function isOverdue(inv: any) {
  return parseFloat(inv.outstanding) > 0 && inv.due_date && inv.due_date < new Date().toISOString().slice(0, 10) && !['paid', 'cancelled'].includes(inv.status)
}

function outstandingClass(inv: any) {
  if (inv.status === 'cancelled') return 'inv-td-muted'
  return parseFloat(inv.outstanding) > 0 ? 'inv-amount inv-amount--red' : 'inv-td-muted'
}

function paidClass(inv: any) {
  if (inv.status === 'cancelled') return 'inv-td-muted'
  return parseFloat(inv.amount_paid) > 0 ? 'inv-amount inv-amount--green' : 'inv-td-muted'
}

function statusLabel(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function statusClass(s: string) {
  const map: Record<string, string> = {
    draft: 'inv-badge--gray',
    sent: 'inv-badge--blue',
    partially_paid: 'inv-badge--amber',
    paid: 'inv-badge--green',
    cancelled: 'inv-badge--red',
  }
  return map[s] ?? 'inv-badge--gray'
}

function downloadCSV() {
  const rows = invoices.value.map((inv: any) => ({
    Invoice:       inv.invoice_number,
    Period:        `${fmtDate(inv.period_start)} – ${fmtDate(inv.period_end)}`,
    Due:           inv.due_date ? fmtDate(inv.due_date) : '',
    Total:         parseFloat(inv.total || 0).toFixed(2),
    'Amount Paid': inv.status === 'cancelled' ? '' : parseFloat(inv.amount_paid || 0).toFixed(2),
    Outstanding:   inv.status === 'cancelled' ? '' : parseFloat(inv.outstanding || 0).toFixed(2),
    Status:        statusLabel(inv.status),
  }))
  const csv = unparse(rows)
  const link = document.createElement('a')
  link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
  link.download = `invoices-${filterYear.value}.csv`
  document.body.appendChild(link); link.click(); document.body.removeChild(link)
}

onMounted(load)
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
* { box-sizing: border-box; }
.inv-page { font-family: 'DM Sans', sans-serif; background: white; min-height: 100%; }
.inv-inner { max-width: 1280px; margin: 0 auto; padding: 0 32px; }
.inv-header { background: white; padding: 24px 0 0; }
.inv-header-inner { display: flex; align-items: center; justify-content: space-between; padding-bottom: 18px; }
.inv-title { font-size: 1.3rem; font-weight: 700; color: #0061eb; margin: 0; letter-spacing: -0.01em; }
.inv-header-actions { display: flex; gap: 8px; align-items: center; }
.inv-select { border: 1.5px solid #ccdeff; border-radius: 8px; padding: 0 12px; height: 38px; font-family: 'DM Sans', sans-serif; font-size: 0.82rem; background: #fff; outline: none; color: #334155; cursor: pointer; }
.inv-select:focus { border-color: #0061eb; }
.inv-btn { height: 38px; padding: 0 16px; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 0.82rem; font-weight: 600; cursor: pointer; border: none; transition: all 0.15s; white-space: nowrap; }
.inv-btn--csv { background: white; color: #16a34a; border: 1.5px solid #22c55e; display: inline-flex; align-items: center; gap: 6px; }
.inv-btn--csv:hover { background: #22c55e; color: white; border-color: #22c55e; }
.inv-btn--csv:active { background: #15803d; border-color: #15803d; color: white; }

.inv-list-wrap { padding-top: 4px; padding-bottom: 48px; }
.inv-table { width: 100%; border: 1.5px solid #ccdeff; border-radius: 12px; overflow: hidden; }
.inv-thead { display: grid; grid-template-columns: 1.6fr 1.7fr 1.2fr 1.1fr 1.1fr 1.1fr 1fr 80px; background: #f0f6ff; border-bottom: 1.5px solid #ccdeff; padding: 0 20px; }
.inv-th { font-size: 0.68rem; font-weight: 700; color: #6b86b8; text-transform: uppercase; letter-spacing: 0.08em; padding: 12px 10px; white-space: nowrap; }
.inv-th--total, .inv-th--paid, .inv-th--outstanding { text-align: right; }
.inv-tbody { display: flex; flex-direction: column; }
.inv-row-wrap { border-bottom: 1px solid #e8f0ff; }
.inv-row-wrap:last-child { border-bottom: none; }
.inv-row { display: grid; grid-template-columns: 1.6fr 1.7fr 1.2fr 1.1fr 1.1fr 1.1fr 1fr 80px; align-items: center; padding: 0 20px; min-height: 56px; transition: background 0.12s; }
.inv-row:hover { background: #f0f6ff; }
.inv-td { padding: 8px 10px; overflow: hidden; }
.inv-td--total, .inv-td--paid, .inv-td--outstanding { text-align: right; }
.inv-ref { font-weight: 700; color: #0061eb; font-size: 0.84rem; letter-spacing: 0.04em; display: block; }
.inv-sub { font-size: 0.7rem; color: #94a3b8; display: block; margin-top: 2px; }
.inv-td-text { font-size: 0.82rem; color: #334155; }
.inv-td-muted { font-size: 0.81rem; color: #94a3b8; }
.inv-amount { font-size: 0.84rem; font-weight: 700; color: #0f172a; }
.inv-amount--red { color: #dc2626; }
.inv-amount--green { color: #059669; }
.inv-overdue { font-size: 0.81rem; color: #dc2626; font-weight: 700; }
.inv-overdue-tag { background: #fee2e2; color: #b91c1c; font-size: 0.62rem; font-weight: 700; padding: 1px 6px; border-radius: 20px; margin-left: 4px; }
.inv-badge { display: inline-block; font-size: 0.68rem; font-weight: 700; padding: 3px 9px; border-radius: 20px; }
.inv-badge--gray { background: #f1f5f9; color: #64748b; }
.inv-badge--blue { background: #dbeafe; color: #1d4ed8; }
.inv-badge--amber { background: #fef3c7; color: #b45309; }
.inv-badge--green { background: #d1fae5; color: #065f46; }
.inv-badge--red { background: #fee2e2; color: #b91c1c; }
.inv-view-btn { display: inline-flex; align-items: center; gap: 4px; font-size: 0.8rem; font-weight: 600; color: #2563eb; background: none; border: none; cursor: pointer; padding: 0; float: right; }
.inv-view-btn:hover { text-decoration: underline; }

.inv-pagination { display: flex; align-items: center; justify-content: center; gap: 16px; padding: 20px 0; }
.inv-page-btn { font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; padding: 8px 18px; border-radius: 8px; border: 1.5px solid #ccdeff; background: white; color: #0061eb; cursor: pointer; transition: border-color 0.15s; }
.inv-page-btn:hover:not(:disabled) { border-color: #0061eb; }
.inv-page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.inv-page-info { font-family: 'DM Sans', sans-serif; font-size: 13px; color: #4a5f82; font-weight: 500; }

.inv-empty { text-align: center; padding: 48px 24px; color: #94a3b8; }
.inv-empty svg { margin: 0 auto 12px; display: block; }
.inv-empty p { font-size: 14px; font-weight: 600; }
.inv-error { text-align: center; padding: 48px 24px; color: #dc2626; }
.inv-error svg { margin: 0 auto 12px; display: block; }
.inv-error p { font-size: 14px; font-weight: 600; margin-bottom: 14px; }
.inv-summary { display: flex; gap: 32px; padding: 16px 20px; background: #f0f6ff; border: 1.5px solid #ccdeff; border-radius: 12px; margin-top: 16px; }
.inv-summary-item { display: flex; flex-direction: column; gap: 2px; }
.inv-summary-label { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #6b86b8; }
.inv-summary-value { font-size: 16px; font-weight: 800; color: #0f172a; }
.inv-summary-value--red { color: #dc2626; }
</style>
